import {Link, Typography} from "@mui/material";
import type {GridColDef} from "@mui/x-data-grid-premium";
import AttachIcon from '~/icons/svg/AttachIcon.svg?react'
import {EMPTY_CELL, formatDate} from "~/components/data-grid/cells/gridCellFormat";
import {centered, subLine} from "~/components/data-grid/cells/gridCells";
import type {ComplianceArtifact} from "~/pages/supplier-360/types/Supplier360Types";
import {
    COMPLIANCE_ARTIFACT_KINDS,
    VERIFICATION_STATUSES,
} from "~/pages/supplier-360/types/Supplier360Types";
import {ARTIFACT_KIND_LABEL} from "~/pages/supplier-360/tabs/SupplierCompliance/constants/complianceLabels";
import {VERIFICATION_CHIP} from "~/pages/supplier-360/tabs/SupplierCompliance/components/complianceVerificationChips";
import {openEvidenceFile} from "~/pages/supplier-360/tabs/SupplierCompliance/components/evidenceTable/openEvidenceFile";

/** `file_size` (bytes) as the design's "1.4MB" / "200KB". */
function formatFileSize(bytes: number | null): string {
    if (bytes === null) return EMPTY_CELL;
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`;
    return `${bytes}B`;
}

/**
 * The record detail page's Evidence table (`sup-compliance.jsx` → `SxcRequirementDetail`).
 * Client-side over the unpaginated `compliance_artifact` list, so every column sorts and filters
 * locally. The file name links to the uploaded document; evidence without a file stays plain text.
 */
export function getEvidenceColumns(): GridColDef<ComplianceArtifact>[] {
    return [
        {
            field: 'name',
            headerName: 'File',
            flex: 1.6,
            minWidth: 240,
            renderCell: ({row}) => centered(
                <>
                    <Typography variant="body2" noWrap sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                        <AttachIcon width={14} height={14}/>
                        {row.file
                            ? (
                                <Link component="button" variant="body2" noWrap onClick={() => void openEvidenceFile(row.id)}>
                                    {row.name}
                                </Link>
                            )
                            : row.name}
                    </Typography>
                    {subLine(formatFileSize(row.file_size))}
                </>,
            ),
        },
        {
            field: 'kind',
            headerName: 'Type',
            type: 'singleSelect',
            valueOptions: COMPLIANCE_ARTIFACT_KINDS.map(value => ({value, label: ARTIFACT_KIND_LABEL[value]})),
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'issued_at',
            headerName: 'Validity',
            type: 'date',
            flex: 1.2,
            minWidth: 200,
            valueGetter: (_value, row) => row.issued_at ? new Date(row.issued_at) : null,
            renderCell: ({row}) => centered(
                <Typography variant="body2">{`${formatDate(row.issued_at)} → ${formatDate(row.expires_at)}`}</Typography>,
            ),
        },
        {
            field: 'uploaded_by',
            headerName: 'Provided by',
            flex: 1.1,
            minWidth: 180,
            renderCell: ({row}) => centered(
                <>
                    <Typography variant="body2" noWrap>{row.uploaded_by || EMPTY_CELL}</Typography>
                    {subLine(formatDate(row.uploaded_at))}
                </>,
            ),
        },
        {
            field: 'verification_status',
            headerName: 'Verification',
            type: 'singleSelect',
            valueOptions: VERIFICATION_STATUSES.map(value => ({value, label: String(VERIFICATION_CHIP[value].props.label)})),
            flex: 1,
            minWidth: 150,
            renderCell: ({row}) => centered(VERIFICATION_CHIP[row.verification_status]),
        },
    ];
}
